import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCodeValidationService } from "shared/hooks/useCodeValidationService";
import { createUpdateSchoolAction } from "shared/state/reducer";
import { Icon, Box, Text, Button, Label } from "@enaep-ng/benchmark";
import { Title, SplitLayout } from "@npd-ng/adp-component-library";
import { Markup } from "interweave";
import { IADPState,AuthenticationProps } from "../typings";

export const AuthenticationView = () => {
  const dispatch = useDispatch();
  const state = useSelector(
    (state: { rootState: IADPState }) => state.rootState
  );
  const { validateCode, clearSchoolDetails } = useCodeValidationService();
  const accessCodeProps: AuthenticationProps = {
    title: state.currentLanguageContent?.accessCode.title || "",
    subTitle: state.currentLanguageContent?.accessCode.subTitle || "",
    input: {
      label: state.currentLanguageContent?.accessCode.inputLabel || "",
      prefix: state.currentLanguageContent?.accessCode.prefix || "",
      verifying: state.currentLanguageContent?.accessCode.verifying || "",
      errorMessage:
        state.currentLanguageContent?.accessCode.codeErrorText || "",
    },
    submitButton: {
      label: state.currentLanguageContent?.actions.submit || "",
    },
    continueButton: {
      label: state.currentLanguageContent?.actions.continue || "",
    },
  };
  const verificationProps: AuthenticationProps = {
    title: state.currentLanguageContent?.verificationCode.title || "",
    subTitle:
      state.currentLanguageContent?.verificationCode.subTitle || "",
    input: {
      label:
        state.currentLanguageContent?.verificationCode.inputLabel || "",
      prefix: state.currentLanguageContent?.verificationCode.prefix || "",
      verifying:
        state.currentLanguageContent?.verificationCode.verifying || "",
      errorMessage:
        state.currentLanguageContent?.verificationCode.codeErrorText ||
        "",
    },
    submitButton: {
      label: state.currentLanguageContent?.actions.submit || "",
    },
    continueButton: {
      label: state.currentLanguageContent?.actions.continue || "",
    },
  };
  const { title, subTitle, input, submitButton } = state.appState.currentView === "AccessCodeValidationView" ? accessCodeProps : verificationProps;
  const [disabled, setDisabled] = useState(true);
 
  // const [selectedLocation, setSelectedLocation] = useState<DropdownOption>({ TODO implement assessment location with device metrics
  //   label: "",
  //   value: "",
  // });

  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const inputVal = e.currentTarget.value;
    const sanitizedValue = inputVal.replace(/\./g, " ");

    dispatch(
      createUpdateSchoolAction({
        status: "validating",
        setupCode:
          state.sessionState.appAccessedFor === "Assess"
            ? sanitizedValue.toUpperCase()
            : undefined,
        verificationCode:
          state.sessionState.appAccessedFor === "Verify"
            ? sanitizedValue.toUpperCase()
            : undefined,
        assessmentLocations: [],
        location: "",
      })
    );
    //dispatch(createSetAssessmentLocationAction(undefined)); TODO add when we add assessment location
  };

  
  const handleSubmit = () => {
    if (!state.sessionState.providedCode) return;
    validateCode(
      state.sessionState.providedCode,
      state.sessionState.appAccessedFor
    );
    setDisabled(true);
  };

  const onKeyUp = (e: any) => {
    if (e && e.target) {
      if (e.key === "Backspace" || e.key === "Delete") {
        setDisabled(true);
      } else {
        if (state?.sessionState?.providedCode?.length === 4) {
          setDisabled(false);
        } else {
          setDisabled(true);
        }
      }
    }
  };
  const onKeyDown = (e: any) => {
    // Allow the default behavior for the Tab key
    if (e.key === "Tab" || e.keyCode === 9) {
      return;
    }
    if (!(/^[a-z0-9]$/i.test(e.key) || e.keyCode === 8)) {
      e.preventDefault();
      return;
    }
    // Explicitly prevent the default action for the space key to avoid any browser or OS-specific behavior
    if (e.key === " " || e.keyCode === 32 || e.key == "Space") {
      e.preventDefault();
      return;
    }
    if (state.sessionState.schoolDetails?.errorCode) {
      clearSchoolDetails();
    }
  };

  return (
    <SplitLayout logoAlt={state.currentLanguageContent?.imageAltText.naepLogo as string} alignment="left">
      <Box
        sx={{
          maxWidth: "calc(526px * var(--zoom))",
          paddingLeft: "calc(22px * var(--zoom))",
          paddingTop: "calc(36px * var(--zoom))",
          paddingBottom: "calc(44px * var(--zoom))",
        }}
      >
        <Box
          sx={{
            paddingBottom: "calc(14px * var(--zoom))",
          }}
        >
          <Title content={title} style={{ textAlign: "left" }} />
        </Box>
        <Box
          style={{
            fontSize: "calc(18px * var(--zoom))",
            color: "#333333",
            marginBottom: "calc(46px  * var(--zoom))",
            marginTop: "0",
          }}
        >
          <Markup content={subTitle} />
        </Box>
        <Box
          sx={{
            display: "flex",
          }}
        >
          <Box
            sx={{
              flex: "50%",
            }}
          >
            <Label
              htmlFor="AuthInput"
              style={{
                fontSize: "calc(24px * var(--zoom))",
                fontWeight: "bold",
                marginBottom: "calc(8px * var(--zoom))",
                display: "block",
              }}
            >
               <Markup content={input.label}/>
            </Label>
            <Box
              sx={{
                position: "relative",
              }}
            >
              <input
                id="AuthInput"
                style={{
                  fontSize: "calc(37px * var(--zoom))",
                  fontWeight: "bold",
                  letterSpacing: "calc(8px * var(--zoom))",
                  lineHeight: "calc(56px * var(--zoom))",
                  paddingLeft: "calc(84px * var(--zoom))",
                  width: "calc(170px * var(--zoom))",
                  fontFamily: "'Calibri', Courier, monospace",
                }}
                maxLength={4}
                value={
                  state.sessionState.providedCode
                    ? state.sessionState.providedCode
                    : ""
                }
                type="text"
                pattern="^[a-zA-Z0-9]+$"
                onKeyUp={onKeyUp}
                onKeyDown={onKeyDown}
                onChange={handleChange}
                tabIndex={0}
              />
              <Text
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "calc(14px  * var(--zoom))",
                  transform: "translateY(-50%)",
                  letterSpacing: "calc(6px * var(--zoom))",
                  fontWeight: "bold",
                  color: "#8a8a8a",
                  fontSize: "calc(32px * var(--zoom))",
                }}
              >
                {input.prefix}
              </Text>
            </Box>
            <br />
            {state.sessionState.schoolDetails?.errorCode && (
              <Text
                sx={{
                  color: "#822727",
                  height: "calc(26.4px * var(--zoom))",
                  fontSize: "calc(15px * var(--zoom))",
                  display: "table",
                  lineHeight: "calc(23px * var(--zoom))",
                }}
              >
                <Markup content={input.errorMessage || ""}>
                  {/* {input.errorMessage || ""} */}
                </Markup>
              </Text>
            )}
            <Button
              disabled={disabled}
              style={{
                backgroundColor: disabled ? "#e4e4e4" : "#0a1971",
                borderRadius: "calc(8px * var(--zoom))",
                color: "#ffffff",
                cursor: disabled ? "default" : "pointer",
                display: "block",
                fontSize: "calc(20px * var(--zoom))",
                height: "calc(57px * var(--zoom))",
                marginTop: "calc(30px * var(--zoom))",
                width: "calc(197px * var(--zoom))",
                border: 'none',
                ...submitButton.style,
              }}
              onClick={handleSubmit}
              tabIndex={disabled ? -1 : 0}
            >
              {state.deviceReadinessState.validationState === "Verifying" ? (
                <>
                  <Box className="spinning-animation">
                    <Icon name="spinner" size="calc(24px * var(--zoom))" />{" "}
                  </Box>
                </>
              ) : (
                submitButton.label
              )}
            </Button>
          </Box>
          {/* TODO readd assessment location with device metrics continueButton && (
            <div
              style={{
                flex: "50%",
              }}>
              <Dropdown {...dropdownTest} onSelect={setSelectedLocation} />

              <button
                style={{
                  backgroundColor: "#0a1971",
                  borderRadius: "8px",
                  color: "#ffffff",
                  cursor: "pointer",
                  display: "block",
                  fontSize: "20px",
                  height: "57px",
                  marginTop: "64.4px",
                  width: "197px",
                  ...continueButton.style,
                }}
                onClick={()=>{}}>
                {continueButton.label}
              </button>
            </div>
          )*/}
        </Box>
      </Box>
    </SplitLayout>
  );
};
